const LOOP_SELECTOR = '[data-home-nk-loop]';
const TRACK_SELECTOR = '[data-home-nk-track]';
const MAIN_GROUP_SELECTOR = '[data-home-nk-group="main"]';
const NEXT_GROUP_SELECTOR = '[data-home-nk-group="next"]';
const PREV_BUTTON_SELECTOR = '[data-home-nk-prev]';
const NEXT_BUTTON_SELECTOR = '[data-home-nk-next]';

/** @type {WeakMap<HTMLElement, () => void>} */
const teardownMap = new WeakMap();

function positiveModulo(value, divisor) {
  return ((value % divisor) + divisor) % divisor;
}

function setupLoop(loopElement) {
  if (!(loopElement instanceof HTMLElement) || teardownMap.has(loopElement)) return;

  const track = loopElement.querySelector(TRACK_SELECTOR);
  const mainGroup = loopElement.querySelector(MAIN_GROUP_SELECTOR);
  const nextGroup = loopElement.querySelector(NEXT_GROUP_SELECTOR);
  const controlsRoot = loopElement.parentElement;
  const prevButton = controlsRoot?.querySelector(PREV_BUTTON_SELECTOR);
  const nextButton = controlsRoot?.querySelector(NEXT_BUTTON_SELECTOR);

  if (!(track instanceof HTMLElement) || !(mainGroup instanceof HTMLElement) || !(nextGroup instanceof HTMLElement)) return;

  let isAdjusting = false;
  let isPointerDown = false;
  let isDragging = false;
  let dragStartX = 0;
  let dragStartScroll = 0;
  let activePointerId = null;
  let suppressClick = false;
  let autoplayFrame = null;
  let autoplayTimer = null;
  let autoplayLastTs = 0;
  let animationFrame = null;
  let dragLastX = 0;
  let dragLastTs = 0;
  let dragVelocity = 0;
  let dragStartIndex = 0;

  const parsePositiveNumber = (value, fallback) => {
    const numeric = Number.parseFloat(value ?? '');
    return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
  };

  const visibleCardsDesktop = parsePositiveNumber(loopElement.dataset.homeNkVisibleDesktop, 5.6);
  const visibleCardsTablet = parsePositiveNumber(loopElement.dataset.homeNkVisibleTablet, 3.4);
  const visibleCardsMobile = parsePositiveNumber(loopElement.dataset.homeNkVisibleMobile, 1.6);
  const autoplayEnabled = ['1', 'true', 'yes', 'on'].includes((loopElement.dataset.homeNkAutoplay || '').toLowerCase());
  const autoplaySpeed = parsePositiveNumber(loopElement.dataset.homeNkAutoplaySpeed, 0);
  const autoplayInterval = parsePositiveNumber(loopElement.dataset.homeNkAutoplayInterval, 5) * 1000;
  const snapEnabled = ['1', 'true', 'yes', 'on'].includes((loopElement.dataset.homeNkSnap || '').toLowerCase());
  const configuredWidthMobile = parsePositiveNumber(loopElement.dataset.homeNkSlideWidthMobile, 0);
  const configuredWidthDesktop = parsePositiveNumber(loopElement.dataset.homeNkSlideWidthDesktop, 0);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** @type {{mainStart: number, nextStart: number, anchorStart: number, nextAnchorStart: number, span: number, step: number} | null} */
  let metrics = null;

  const syncSlideWidth = () => {
    if (configuredWidthMobile > 0 || configuredWidthDesktop > 0) {
      const configuredWidth = window.matchMedia('(min-width: 750px)').matches
        ? configuredWidthDesktop || configuredWidthMobile
        : configuredWidthMobile || configuredWidthDesktop;
      const maximumWidth = (loopElement.clientWidth || window.innerWidth) * 0.88;
      loopElement.style.setProperty('--home-nk-slide-width-px', `${Math.min(configuredWidth, maximumWidth)}px`);
      return;
    }

    const styles = window.getComputedStyle(loopElement);
    const gapRaw = styles.getPropertyValue('--slideshow-gap').trim();
    const slideWidthRaw = styles.getPropertyValue('--home-nk-slide-width').trim();
    const gap = parseFloat(gapRaw) || 0;
    const slideWidth = parseFloat(slideWidthRaw);

    // Keep cards width stable in pixels to avoid overlap caused by percentage-width rounding.
    if (slideWidth > 0) {
      const fontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      const gapPx = gapRaw.endsWith('rem') ? gap * fontSize : gap;
      const containerWidth = loopElement.clientWidth || 0;
      if (containerWidth > 0) {
        const visibleCards =
          window.matchMedia('(min-width: 990px)').matches
            ? visibleCardsDesktop
            : window.matchMedia('(min-width: 750px)').matches
              ? visibleCardsTablet
              : visibleCardsMobile;
        const widthPx = (containerWidth - gapPx * (visibleCards - 1)) / visibleCards;
        if (widthPx > 0) {
          loopElement.style.setProperty('--home-nk-slide-width-px', `${widthPx}px`);
        }
      }
    }
  };

  const measure = (preservePosition = true) => {
    syncSlideWidth();

    const previousOffset = metrics ? loopElement.scrollLeft - metrics.anchorStart : 0;

    const mainStart = mainGroup.offsetLeft;
    const nextStart = nextGroup.offsetLeft;
    const span = nextStart - mainStart;
    const slides = mainGroup.querySelectorAll('.nk-centered-gallery__slide, .home-nk__product-slide');
    const firstSlide = slides[0];
    const secondSlide = slides[1];
    const slideWidth = firstSlide instanceof HTMLElement ? firstSlide.getBoundingClientRect().width : 0;
    const step =
      firstSlide instanceof HTMLElement && secondSlide instanceof HTMLElement
        ? secondSlide.offsetLeft - firstSlide.offsetLeft
        : slideWidth;
    const centerOffset = snapEnabled ? Math.max((loopElement.clientWidth - slideWidth) / 2, 0) : 0;
    const anchorStart = mainStart - centerOffset;
    const nextAnchorStart = nextStart - centerOffset;

    if (span <= 0 || step <= 0) return;

    metrics = { mainStart, nextStart, anchorStart, nextAnchorStart, span, step };

    isAdjusting = true;

    if (preservePosition) {
      loopElement.scrollLeft = anchorStart + positiveModulo(previousOffset, span);
    } else {
      loopElement.scrollLeft = anchorStart;
    }

    isAdjusting = false;
  };

  const normalizeScroll = () => {
    if (!metrics || isAdjusting) return;

    const { anchorStart, nextAnchorStart, span } = metrics;
    const current = loopElement.scrollLeft;

    if (current < anchorStart) {
      isAdjusting = true;
      loopElement.scrollLeft = current + span;
      isAdjusting = false;
      return;
    }

    if (current >= nextAnchorStart) {
      isAdjusting = true;
      loopElement.scrollLeft = current - span;
      isAdjusting = false;
    }
  };

  const cancelAnimation = () => {
    if (animationFrame !== null) {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }
  };

  const animateTo = (target, duration = 420) => {
    cancelAnimation();
    const start = loopElement.scrollLeft;
    const distance = target - start;

    if (reduceMotion || Math.abs(distance) < 1) {
      loopElement.scrollLeft = target;
      normalizeScroll();
      return;
    }

    const startedAt = performance.now();
    const tick = (timestamp) => {
      const progress = Math.min((timestamp - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      loopElement.scrollLeft = start + distance * eased;

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(tick);
      } else {
        animationFrame = null;
        loopElement.scrollLeft = target;
        normalizeScroll();
      }
    };

    animationFrame = window.requestAnimationFrame(tick);
  };

  const snapToPosition = (projectedPosition = loopElement.scrollLeft, originIndex = null) => {
    if (!metrics) return;
    let targetIndex = Math.round((projectedPosition - metrics.anchorStart) / metrics.step);
    if (originIndex !== null) {
      targetIndex = Math.max(originIndex - 1, Math.min(originIndex + 1, targetIndex));
    }
    animateTo(metrics.anchorStart + targetIndex * metrics.step);
  };

  const moveBy = (direction) => {
    if (!metrics) return;
    const currentIndex = Math.round((loopElement.scrollLeft - metrics.anchorStart) / metrics.step);
    animateTo(metrics.anchorStart + (currentIndex + direction) * metrics.step);
  };

  const onPointerDown = (event) => {
    if (!(event instanceof PointerEvent) || event.button !== 0) return;

    isPointerDown = true;
    cancelAnimation();
    isDragging = false;
    dragStartX = event.clientX;
    dragStartScroll = loopElement.scrollLeft;
    dragLastX = event.clientX;
    dragLastTs = event.timeStamp;
    dragVelocity = 0;
    dragStartIndex = metrics ? Math.round((dragStartScroll - metrics.anchorStart) / metrics.step) : 0;
    activePointerId = event.pointerId;
  };

  const onPointerMove = (event) => {
    if (!isPointerDown || !(event instanceof PointerEvent)) return;

    const deltaX = event.clientX - dragStartX;
    if (!isDragging) {
      // Treat as drag only after clear horizontal intent.
      if (Math.abs(deltaX) < 8) return;
      isDragging = true;
      suppressClick = true;
      loopElement.classList.add('is-dragging');
      if (activePointerId != null) {
        loopElement.setPointerCapture(activePointerId);
      }
    }

    const elapsed = event.timeStamp - dragLastTs;
    if (elapsed > 0) {
      const instantaneousVelocity = (dragLastX - event.clientX) / elapsed;
      dragVelocity = dragVelocity * 0.55 + instantaneousVelocity * 0.45;
      dragLastX = event.clientX;
      dragLastTs = event.timeStamp;
    }

    loopElement.scrollLeft = dragStartScroll - deltaX;
  };

  const endPointerDrag = (event) => {
    if (!isPointerDown || !(event instanceof PointerEvent)) return;

    isPointerDown = false;

    if (isDragging) {
      isDragging = false;
      loopElement.classList.remove('is-dragging');

      if (loopElement.hasPointerCapture(event.pointerId)) {
        loopElement.releasePointerCapture(event.pointerId);
      }

      if (snapEnabled) {
        snapToPosition(loopElement.scrollLeft + dragVelocity * 180, dragStartIndex);
      } else {
        normalizeScroll();
      }
    }

    activePointerId = null;
  };

  const onClickCapture = (event) => {
    if (suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }
  };

  const shouldAutoplay = () =>
    autoplayEnabled && !isPointerDown && !isDragging && !loopElement.matches(':hover') && !loopElement.matches(':focus-within') && !document.hidden;

  const autoplayTick = (timestamp) => {
    if (autoplayLastTs === 0) autoplayLastTs = timestamp;
    const elapsedSeconds = Math.min((timestamp - autoplayLastTs) / 1000, 0.05);
    autoplayLastTs = timestamp;

    if (shouldAutoplay() && autoplaySpeed > 0) {
      loopElement.scrollLeft += autoplaySpeed * elapsedSeconds;
      normalizeScroll();
    }

    autoplayFrame = window.requestAnimationFrame(autoplayTick);
  };

  const onPrevClick = () => moveBy(-1);
  const onNextClick = () => moveBy(1);

  const onScroll = () => {
    if (!isPointerDown && animationFrame === null) normalizeScroll();
  };
  const onResize = () => measure(true);
  const onLoad = () => measure(true);

  const resizeObserver = new ResizeObserver(() => {
    measure(true);
  });

  requestAnimationFrame(() => {
    measure(false);
  });

  loopElement.addEventListener('scroll', onScroll, { passive: true });
  loopElement.addEventListener('pointerdown', onPointerDown);
  loopElement.addEventListener('pointermove', onPointerMove);
  loopElement.addEventListener('pointerup', endPointerDrag);
  loopElement.addEventListener('pointercancel', endPointerDrag);
  loopElement.addEventListener('lostpointercapture', endPointerDrag);
  loopElement.addEventListener('click', onClickCapture, true);
  window.addEventListener('resize', onResize);
  window.addEventListener('load', onLoad);
  resizeObserver.observe(loopElement);
  resizeObserver.observe(mainGroup);

  if (snapEnabled && autoplayEnabled) {
    autoplayTimer = window.setInterval(() => {
      if (shouldAutoplay()) moveBy(1);
    }, autoplayInterval);
  } else if (autoplayEnabled && autoplaySpeed > 0) {
    autoplayFrame = window.requestAnimationFrame(autoplayTick);
  }

  if (prevButton instanceof HTMLButtonElement) prevButton.addEventListener('click', onPrevClick);
  if (nextButton instanceof HTMLButtonElement) nextButton.addEventListener('click', onNextClick);

  const teardown = () => {
    loopElement.removeEventListener('scroll', onScroll);
    loopElement.removeEventListener('pointerdown', onPointerDown);
    loopElement.removeEventListener('pointermove', onPointerMove);
    loopElement.removeEventListener('pointerup', endPointerDrag);
    loopElement.removeEventListener('pointercancel', endPointerDrag);
    loopElement.removeEventListener('lostpointercapture', endPointerDrag);
    loopElement.removeEventListener('click', onClickCapture, true);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('load', onLoad);
    resizeObserver.disconnect();
    cancelAnimation();
    if (autoplayFrame !== null) {
      window.cancelAnimationFrame(autoplayFrame);
    }
    if (autoplayTimer !== null) {
      window.clearInterval(autoplayTimer);
    }
    if (prevButton instanceof HTMLButtonElement) prevButton.removeEventListener('click', onPrevClick);
    if (nextButton instanceof HTMLButtonElement) nextButton.removeEventListener('click', onNextClick);
    loopElement.classList.remove('is-dragging');
  };

  teardownMap.set(loopElement, teardown);
}

function initInRoot(root) {
  if (!(root instanceof Element) && !(root instanceof Document)) return;
  root.querySelectorAll(LOOP_SELECTOR).forEach((el) => setupLoop(el));
}

function teardownInRoot(root) {
  if (!(root instanceof Element) && !(root instanceof Document)) return;
  root.querySelectorAll(LOOP_SELECTOR).forEach((el) => {
    const teardown = teardownMap.get(el);
    if (teardown) {
      teardown();
      teardownMap.delete(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initInRoot(document));
} else {
  initInRoot(document);
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof Element) initInRoot(target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const target = event.target;
  if (target instanceof Element) teardownInRoot(target);
});
